import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play, Shield, BarChart3, Pause } from 'lucide-react';

// Using a more professional background image and desktop video for all devices
const heroBg = require('../assets/business-analyst-looking-into-statistics-reports-detect-any-obstacles_482257-110400.jpg_qxkemx.jpg');
const heroVideo = require('../assets/livevid-desktop.mp4');

const Hero = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [videoRef, setVideoRef] = useState(null);

  const togglePlayPause = () => {
    if (videoRef) {
      if (isPlaying) {
        videoRef.pause();
      } else {
        videoRef.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoRef = (ref) => {
    setVideoRef(ref);
    if (ref) {
      ref.addEventListener('play', () => setIsPlaying(true));
      ref.addEventListener('pause', () => setIsPlaying(false));
    }
  };

  return (
    <section id="home" className="hero-section-clean">
      {/* Enhanced Background */}
      <div className="hero-background">
        <img
          src={heroBg}
          alt="Professional Trading Environment"
          className="hero-background-image"
        />
        <div className="hero-background-overlay" />
        {/* Green-themed gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-secondary-900/95 via-secondary-900/85 to-emerald-900/70" />
      </div>

      {/* Content Container - Clean Layout */}
      <div className="hero-container-clean">
        <div className="hero-content-grid">
          
          {/* Left Content - Clean and Organized */}
          <div className="hero-left-content">
            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="hero-badge-clean"
            >
              <Shield className="hero-badge-icon-clean" />
              <span>Trusted Brokerage Platform</span>
            </motion.div>

            {/* Main Headlines - Clean and Proportioned */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hero-title-clean"
            >
              <span className="hero-title-primary-clean">ChartLine</span>
              <br />
              <span className="hero-title-gradient-clean">Brokerage</span>
            </motion.h1>

            {/* Subheadline - Clean and Readable */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="hero-subtitle-clean"
            >
              Your premier brokerage partner for stocks, crypto, and global markets. Experience professional trading with cutting-edge technology and expert support.
            </motion.p>

            {/* CTA Buttons - Clean Layout */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="hero-cta-container-clean"
            >
              <motion.a
                href="https://dashboard.chartlinebrokerage.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(34, 197, 94, 0.3)' }}
                whileTap={{ scale: 0.95 }}
                className="hero-cta-primary-clean group"
              >
                <span>Register</span>
                <ArrowRight className="hero-cta-icon-clean group-hover:translate-x-1" />
              </motion.a>
              
              <motion.a
                href="https://dashboard.chartlinebrokerage.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="hero-cta-secondary-clean group"
              >
                <BarChart3 className="hero-cta-icon-clean" />
                <span>Login</span>
              </motion.a>
            </motion.div>

            {/* Features - Clean List */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="hero-features-clean"
            >
              <div className="hero-feature-item-clean">
                <div className="hero-feature-dot-clean" />
                <span>Zero Commission Trading</span>
              </div>
              <div className="hero-feature-item-clean">
                <div className="hero-feature-dot-clean" />
                <span>Professional Grade Tools</span>
              </div>
            </motion.div>
          </div>

          {/* Right Content - Clean Video Layout */}
          <div className="hero-right-content">
            {/* Live Dashboard Video Section */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="hero-video-container-clean"
            >
              <div className="live-dashboard-wrapper-clean group">
                {/* Video Frame with Clean Styling */}
                <div className="live-dashboard-frame-clean">
                  <div className="live-dashboard-header-clean">
                    <div className="live-dashboard-dots-clean">
                      <div className="dot dot-red"></div>
                      <div className="dot dot-yellow"></div>
                      <div className="dot dot-green"></div>
                    </div>
                    <div className="live-dashboard-title-clean">
                      <span>ChartLine Trading Platform</span>
                      <div className="live-indicator-clean">
                        <div className="live-dot"></div>
                        <span>LIVE</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Clean Video */}
                  <div className="live-dashboard-screen-clean">
                    <video
                      ref={handleVideoRef}
                      className="live-dashboard-video-clean"
                      src={heroVideo}
                      autoPlay
                      loop
                      muted
                      playsInline
                      poster={heroBg}
                    />
                    
                    {/* Interactive Video Overlay */}
                    <div className="live-dashboard-overlay-clean group-hover:opacity-100">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={togglePlayPause}
                        className="live-dashboard-play-btn-clean"
                      >
                        {isPlaying ? (
                          <Pause className="live-dashboard-play-icon-clean" />
                        ) : (
                          <Play className="live-dashboard-play-icon-clean" />
                        )}
                      </motion.button>
                    </div>
                  </div>
                  
                  {/* Clean Dashboard Info Cards */}
                  <div className="dashboard-info-cards-clean">
                    <div className="info-card-clean">
                      <div className="info-icon-clean bg-emerald-500/20">
                        <BarChart3 className="h-4 w-4 text-emerald-400" />
                      </div>
                      <div className="info-content-clean">
                        <span className="info-title-clean">Real-Time Data</span>
                        <span className="info-subtitle-clean">Live market feeds</span>
                      </div>
                    </div>
                    <div className="info-card-clean">
                      <div className="info-icon-clean bg-blue-500/20">
                        <Shield className="h-4 w-4 text-blue-400" />
                      </div>
                      <div className="info-content-clean">
                        <span className="info-title-clean">Secure Trading</span>
                        <span className="info-subtitle-clean">Bank-level security</span>
                      </div>
                    </div>
                  </div>

                  {/* Integrated Stats Section - Part of Video Frame */}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.0 }}
                    className="hero-stats-integrated"
                  >
                    <div className="hero-stat-item-clean">
                      <div className="hero-stat-number-clean">50K+</div>
                      <div className="hero-stat-label-clean">Active Users</div>
                    </div>
                    <div className="hero-stat-divider-clean" />
                    <div className="hero-stat-item-clean">
                      <div className="hero-stat-number-clean">99.9%</div>
                      <div className="hero-stat-label-clean">Uptime</div>
                    </div>
                    <div className="hero-stat-divider-clean" />
                    <div className="hero-stat-item-clean">
                      <div className="hero-stat-number-clean">24/7</div>
                      <div className="hero-stat-label-clean">Support</div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Clean Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="hero-scroll-indicator-clean"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="hero-scroll-mouse-clean"
        >
          <div className="hero-scroll-wheel-clean" />
        </motion.div>
        <span className="hero-scroll-text-clean">Explore platform</span>
      </motion.div>
    </section>
  );
};

export default Hero; 